import { createStore } from "zustand/vanilla";
import { conflict, err, ok } from "@pack-it-up/shared";
import { computeRefreshDraft, planRefresh, refreshCounts, refreshEntryKey, } from "@pack-it-up/core";
import { errorToMessage } from "../errorToMessage.js";
// Doc 14 §7 — refresh writes trip rows and only *reads* the template, so unlike
// `syncCandidates` there is no role test: every unarchived template this trip has
// imported and this user can still see is a candidate.
export const refreshCandidates = (trip, mine) => mine.filter((t) => !t.archived && trip.importedTemplateIds.includes(t.id));
const seedChecked = (draft) => {
    const out = new Set();
    for (const e of draft.entries)
        if (e.defaultChecked)
            out.add(refreshEntryKey(e));
    return out;
};
// Rows the dialog never lists: identical on both sides, nothing to decide.
const actionable = (draft) => draft.entries.filter((e) => e.status !== "unchanged");
const ZERO = { adds: 0, updates: 0, conflicts: 0 };
export const createTemplateRefreshStore = (deps) => {
    // The inputs the planners need. Not UI state — kept out of the store's shape so a
    // re-render never depends on them.
    let trip = null;
    let tripContents = { categories: [], items: [], todos: [] };
    let template = null;
    const closedState = () => ({
        open: false,
        templateId: null,
        templateName: "",
        draft: null,
        checked: new Set(),
        loading: false,
        error: null,
    });
    return createStore((set, get) => ({
        ...closedState(),
        summary: null,
        counts() {
            const { draft, checked } = get();
            return draft ? refreshCounts(draft, checked) : ZERO;
        },
        upToDate() {
            const { draft, loading } = get();
            return !loading && draft !== null && actionable(draft).length === 0;
        },
        async openFor(nextTrip, contents, summary) {
            trip = nextTrip;
            tripContents = contents;
            template = null;
            set({
                ...closedState(),
                open: true,
                templateId: summary.id,
                templateName: summary.name,
                loading: true,
                summary: null,
            });
            const r = await deps.templates.fetchTemplateContents(summary.id);
            // A second open landing while this fetch was in flight owns the dialog now.
            if (get().templateId !== summary.id)
                return;
            if (!r.ok) {
                set({ loading: false, error: errorToMessage(r.error) });
                return;
            }
            template = r.value;
            const draft = computeRefreshDraft(summary.id, nextTrip, contents, r.value, deps.actor);
            set({ draft, checked: seedChecked(draft), loading: false, error: null });
        },
        toggle(key) {
            const next = new Set(get().checked);
            if (next.has(key))
                next.delete(key);
            else
                next.add(key);
            set({ checked: next });
        },
        checkAll() {
            const { draft } = get();
            if (!draft)
                return;
            set({ checked: new Set(actionable(draft).map(refreshEntryKey)) });
        },
        uncheckAll() {
            set({ checked: new Set() });
        },
        confirm() {
            const { draft, checked, templateId, templateName } = get();
            if (!draft || !trip || !template || templateId == null) {
                const error = conflict("No template selected.");
                set({ error: errorToMessage(error) });
                return err(error);
            }
            const counts = refreshCounts(draft, checked);
            // Nothing ticked is a no-op, not an error — the user closed an empty dialog.
            if (counts.adds > 0 || counts.updates > 0) {
                const plan = planRefresh(draft, trip, tripContents, template, checked, deps.actor);
                deps.applyRefreshPlan(plan, trip.id);
            }
            const result = { templateId, templateName, counts };
            trip = null;
            template = null;
            tripContents = { categories: [], items: [], todos: [] };
            set({ ...closedState(), summary: result });
            return ok(result);
        },
        cancel() {
            trip = null;
            template = null;
            tripContents = { categories: [], items: [], todos: [] };
            set({ ...closedState(), summary: null });
        },
    }));
};
//# sourceMappingURL=templateRefreshStore.js.map